"use client";

import { useT } from "@/lib/i18n/context";
import type { WFaqContent } from "@/lib/api/cms-window-content";
import { wFaqTranslations } from "./WFaq.i18n";
import styles from "./WFaq.module.css";

/**
 * WFaq - okno "FAQ" (akordeon pytan/odpowiedzi).
 *
 * Backend `W_DEDICATED_FAQ_CONTROLLER::getApiContent()` zwraca opublikowane wpisy z katalogu
 * zrodlowego okna (`imp_cms_window.id_catalog`, `c_faq.public = 1`). Sort po `p.sort`.
 *
 * Rendering: natywny `<details>`/`<summary>` - zero JS state, dostepnosc klawiatury
 * z pudelka, native chevron marker (rotowany CSS-em). `answer` = HTML z TinyMCE
 * (trusted admin source) - `dangerouslySetInnerHTML`.
 */
export function WFaq({ content, title }: WFaqProps) {
  const t = useT(wFaqTranslations);

  if (!content || !Array.isArray(content.items) || content.items.length === 0) {
    return null;
  }

  const heading = title || t.defaultTitle;
  const eyebrow = (content.hero_text ?? "").trim();

  return (
    <section className={styles.root}>
      <div className={styles.header}>
        {eyebrow !== "" ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
        {heading ? <h2 className={styles.heading}>{heading}</h2> : null}
      </div>

      <ul className={styles.list}>
        {content.items.map((item) => (
          <li key={item.id} className={styles.row}>
            <details className={styles.details}>
              <summary className={styles.question}>{item.question}</summary>
              <div
                className={styles.answer}
                dangerouslySetInnerHTML={{ __html: item.answer }}
              />
            </details>
          </li>
        ))}
      </ul>
    </section>
  );
}

export type WFaqProps = {
  content: WFaqContent | null;
  title: string;
};

export default WFaq;
